import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const commodities = [
  { id: "wheat-flour", name: "Wheat Flour", image: "/commodities/wheat-flour.png", quantity: "100 MT", packaging: "50 kg Bags", destination: "Kenya" },
  { id: "sunflower-oil", name: "Sunflower Oil", image: "/commodities/sunflower-oil.png", quantity: "80 MT", packaging: "5 L Bottles", destination: "UAE" },
  { id: "spaghetti", name: "Spaghetti", image: "/commodities/spaghetti.png", quantity: "60 MT", packaging: "Industrial Cartons", destination: "Iraq" },
  { id: "bulgur", name: "Bulgur", image: "/commodities/bulgur.png", quantity: "45 MT", packaging: "Bulk Bags", destination: "Somalia" },
  { id: "semolina", name: "Semolina", image: "/commodities/semolina.png", quantity: "35 MT", packaging: "25 kg Sacks", destination: "Kenya" },
];

const participants = [
  { id: "Supplier A-104", status: "Offer Submitted" },
  { id: "Supplier B-208", status: "Qualified" },
  { id: "Supplier C-317", status: "Participating" },
];

const statusStyles = {
  "Offer Submitted": "bg-emerald-50 text-emerald-700",
  Qualified: "bg-[#0B1F3A]/[0.06] text-[#0B1F3A]",
  Participating: "bg-[#3B82F6]/10 text-[#1D4ED8]",
};

export default function DashboardMock() {
  const [offers, setOffers] = useState(6);
  const [selectedId, setSelectedId] = useState("wheat-flour");
  const active = commodities.find((c) => c.id === selectedId) || commodities[0];

  useEffect(() => {
    const t = setTimeout(() => setOffers(7), 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="hero-dashboard-mock"
      className="relative w-full rounded-[24px] bg-white border border-slate-200/80 cb-elevated-shadow overflow-hidden"
    >
      {/* Chrome */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-[#FAFBFD]">
        <div className="flex items-center gap-2.5">
          <span className="hidden sm:inline text-[11px] font-medium text-[#475569]">
            demaxtore.com/workspace/<span className="text-[#0B1F3A] font-semibold">commoditybid</span>
          </span>
        </div>
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 cb-pulse" />
          <span className="text-[11px] font-semibold text-emerald-700 uppercase">Live</span>
        </span>
      </div>

      <div className="p-5 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <p className="font-inter text-[10px] font-bold tracking-[0.2em] uppercase text-[#3B82F6]">
              Live Procurement Bidding · #CB-2148
            </p>
            <h3 className="mt-1 font-display text-[17px] font-bold text-[#0B1F3A] tracking-tight">
              Food Commodity Import Request
            </h3>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] font-medium text-[#64748B]">Closes in</p>
            <p className="font-display font-bold text-[15px] text-[#0B1F3A] tabular-nums">11h 42m</p>
          </div>
        </div>

        {/* Commodity strip */}
        <div className="flex gap-2.5 overflow-x-auto pb-1 mb-5">
          {commodities.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className={`shrink-0 w-[108px] rounded-xl border p-2.5 text-left transition-all ${
                c.id === selectedId
                  ? "border-[#3B82F6]/50 bg-[#3B82F6]/[0.04] shadow-sm"
                  : "border-slate-100 bg-[#F8FAFC] hover:border-slate-200"
              }`}
            >
              <div className="h-14 w-full rounded-lg overflow-hidden bg-white border border-slate-100">
                <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
              </div>
              <p className="mt-2 font-display font-bold text-[11px] text-[#0B1F3A] truncate">{c.name}</p>
              <p className="text-[10px] text-[#64748B] mt-0.5">{c.quantity}</p>
            </button>
          ))}
        </div>

        {/* Active detail + stats */}
        <div className="grid sm:grid-cols-[1.4fr_1fr] gap-3 mb-5">
          <motion.div
            key={active.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-3.5 rounded-xl border border-slate-100 bg-[#F8FAFC] px-4 py-3.5"
          >
            <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden border border-slate-200/80 bg-white">
              <img src={active.image} alt={active.name} className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-[15px] text-[#0B1F3A]">{active.name}</p>
              <p className="mt-1 text-[12px] text-[#475569]">
                {active.quantity} · {active.packaging}
              </p>
              <p className="mt-0.5 text-[12px] text-[#64748B]">Destination: {active.destination}</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Invited", value: 12 },
              { label: "Active", value: 9, live: true },
              { label: "Offers", value: offers, animate: true },
            ].map(({ label, value, live, animate }) => (
              <div
                key={label}
                className="rounded-xl border border-slate-100 bg-white px-2.5 py-3 text-center"
              >
                {animate ? (
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={value}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="font-display font-bold text-[18px] text-[#0B1F3A] tabular-nums leading-none"
                    >
                      {value}
                    </motion.p>
                  </AnimatePresence>
                ) : (
                  <p className={`font-display font-bold text-[18px] tabular-nums leading-none ${
                    live ? "text-emerald-700" : "text-[#0B1F3A]"
                  }`}>
                    {value}
                  </p>
                )}
                <p className="mt-1 text-[10px] font-medium text-[#64748B]">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Supplier list — minimal */}
        <div className="rounded-xl border border-slate-100 overflow-hidden">
          {participants.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.06 }}
              className="flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-0"
            >
              <p className="font-mono font-semibold text-[12px] text-[#0B1F3A]">{p.id}</p>
              <span className={`text-[10.5px] font-semibold px-2 py-1 rounded-md ${
                statusStyles[p.status] || statusStyles.Participating
              }`}>
                {p.status}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
